/* eslint no-use-before-define:0, no-param-reassign: 0 */

/**
 * The TimingUtils offers functions to find the closest timesteps
 * and buffering Tiles.
 */

import L from 'leaflet';
import * as Constants from '../DataLayer/Utils/Constants';
import { updateBufferStatus, toggleBufferStatus } from './TimingState';

/**
 * Returns currentIndex as a Object and arrays with before- and after-indices
 * @param  {Array} timesteps - objects with timestep data
 * @param  {Integer} timestamp - current timestamp
 * @param  {Integer} beforeCount - number of before indices
 * @param  {Integer} afterCount - number of after indices
 * @return {Object}
 * Returns { nearestTimestep: {...}, beforeTimesteps: [{...}], afterTimesteps: [{...}] }
 */
export function getClosestTimeSteps(timesteps, timestamp, beforeCount = 2, afterCount = 2) {
  let minTimeDiff = Number.MAX_VALUE;
  let beforeTimesteps = [];
  let afterTimesteps = [];
  let nearestIndex = 0;

  switch (timesteps.length) {
    case 4:
      beforeCount = 1;
      break;
    case 3:
      beforeCount = 1;
      afterCount = 1;
      break;
    case 2:
      beforeCount = 0;
      afterCount = 1;
      break;
    case 1:
      beforeCount = 0;
      afterCount = 0;
      break;
    default:
      break;
  }

  // extend timesteps with timedifference, for finding the nearest index
  const extendedTimesteps = timesteps.slice(0).map((ts, index) => {
    ts.timeDiff = Math.abs(+new Date(ts.date) - timestamp);
    ts.index = index;
    minTimeDiff = ts.timeDiff < minTimeDiff ? ts.timeDiff : minTimeDiff;
    return ts;
  });

  nearestIndex = extendedTimesteps.filter(ts => ts.timeDiff === minTimeDiff)[0].index;

  if (nearestIndex > 0 && beforeCount > 0) {
    let sliceFrom = nearestIndex - beforeCount;
    sliceFrom = sliceFrom < 0 ? 0 : sliceFrom;
    beforeTimesteps = extendedTimesteps.slice(sliceFrom, nearestIndex);
  }

  if (nearestIndex < extendedTimesteps.length - 1 && afterCount > 0) {
    afterTimesteps = extendedTimesteps.slice(nearestIndex + 1, nearestIndex + 1 + afterCount);
  }

  if (beforeTimesteps.length < beforeCount) {
    for (let index = 0; index <= beforeCount - beforeTimesteps.length; index++) {
      beforeTimesteps.splice(0, 0, extendedTimesteps[extendedTimesteps.length - (index + 1)]);
    }
  }
  if (afterTimesteps.length < afterCount) {
    for (let index = 0; index <= afterCount - afterTimesteps.length; index++) {
      afterTimesteps.splice(afterTimesteps.length, 0, extendedTimesteps[index]);
    }
  }

  return {
    nearestTimestep: extendedTimesteps[nearestIndex],
    beforeTimesteps,
    afterTimesteps
  };
}

/**
 * Buffers all tiles that are specified in the timestep variable.
 * You also need to pass a leafletmap, because we add the buffer layer there.
 * @param  {array} timesteps  Array with objects that include the tile urls
 * @param  {object} leafletMap A Leaflet map
 * @param  {function} callback for updating loading tiles progress for ui
 * @param  {function} callback for updating loading status in AppState
 * @return {object} Returns the tilelayer that is used to buffer the tiles.
 */
export function bufferTiles(timesteps, leafletMap, layerType, urlKey, store, toggleLoadingApp) {
  // console.log('%cbufferTiles', 'background:red;color:white');
  store.dispatch(toggleBufferStatus());
  store.dispatch(toggleLoadingApp());

  const tilesBuffer = [].concat(timesteps);
  const bufferLayer = createBufferLayer(layerType);
  const initialIndex = getClosestTimeSteps(timesteps, store.getState().TimingState.currentTimestamp).nearestTimestep.index;
  let prevIndex = 0;
  // get index of initial timestep
  let currentIndex = initialIndex;
  // console.log('%cbufferTiles: currentIndex ' + currentIndex, 'background:orange;');

  bufferLayer.on('load', () => loadTiles());
  bufferLayer.addTo(leafletMap);

  loadTiles();

  return bufferLayer;

  function loadTiles() {
    const timingState = store.getState().TimingState;
    const allTilesLoaded = timingState.tilesLoaded.every(t => t);
    const allTilesLoadedInIdleMode = timingState.idleMode && currentIndex > initialIndex + timingState.initialBufferLoadCount;

    // console.log(`${timingState.tilesLoaded.map(i => (i ? '█' : '░'))} | initial: ${initialIndex}, current: ${currentIndex} (loaded?: ${timingState.tilesLoaded[currentIndex]}), idle: ${timingState.idleMode}, bufferLoadCount: ${timingState.initialBufferLoadCount} | ${allTilesLoaded} ${allTilesLoadedInIdleMode}`);

    // all tiles are loaded
    if (allTilesLoaded || allTilesLoadedInIdleMode) {
      store.dispatch(toggleBufferStatus());
      store.dispatch(toggleLoadingApp());
      // console.warn(`return because ${allTilesLoaded ? 'allTilesLoaded' : ''} ${allTilesLoadedInIdleMode ? 'allTilesLoadedInIdleMode' : ''} = true`);
      return false;
    }

    // buffer reached end of bar, but not tiles are loaded
    if (currentIndex === tilesBuffer.length) {
      currentIndex = 0;
    }

    // get timeStep from UI State
    const currentTimeStepIndex = getClosestTimeSteps(timesteps, timingState.currentTimestamp).nearestTimestep.index;

    // if user clicks in time slider while buffering, we start buffering from there
    if (currentTimeStepIndex !== prevIndex && currentIndex !== 0) {
      prevIndex = currentTimeStepIndex;
      currentIndex = currentTimeStepIndex;
    }

    // when tile is already loaded we skip to the next one
    if (timingState.tilesLoaded[currentIndex]) {
      currentIndex++;
      loadTiles();
      return false;
    }

    bufferLayer.setUrl(`${Constants.TILE_HOST}${tilesBuffer[currentIndex][urlKey]}`);

    // current loaded tile
    store.dispatch(updateBufferStatus(currentIndex));

    currentIndex++;
  }
}

/**
 * Returns Leafletlayer from type tileLayer or imageLayer
 * @param  {String} layerType
 * @return {Object} empty Leaflet Layer
 */
function createBufferLayer(_layerType) {
  let layer;

  if (_layerType === 'tileLayer') {
    layer = L.tileLayer(' ', {
      opacity: 0,
    });
  } else if (_layerType === 'imageOverlay') {
    layer = L.imageOverlay(' ', [[54.9926451, 15.8785965], [46.8541934, 1.9006908]], {
      opacity: 0,
    });
  }

  return layer;
}



/** WEBPACK FOOTER **
 ** ./core_components/TimingHandler/TimingUtils.js
 **/